import { useEffect, useState } from "react";
import utils from "../../universal/utils";

const CACHE_TTL_MS = 60_000;
const previewCache = new Map();

function toFiniteNumber(value) {
    if (value == null || value === "") return null;
    const parsed = typeof value === "number" ? value : Number.parseFloat(String(value));
    return Number.isFinite(parsed) ? parsed : null;
}

export function resolvePlayerSteamId(player) {
    return (
        player?.steamId ||
        player?.steamId64 ||
        player?.steam64Id ||
        null
    );
}

function cacheKey(player) {
    return String(player?._id || player?.userId || player?.steamId || "");
}

export function formatKd(value) {
    const n = toFiniteNumber(value);
    if (n == null) return "—";
    return n.toFixed(2);
}

export function formatRating3(value) {
    const n = toFiniteNumber(value);
    if (n == null) return "—";
    return n.toFixed(2);
}

export function formatReputation(value) {
    const n = toFiniteNumber(value);
    if (n == null) return "—";
    return String(Math.round(n));
}

export function resolveReputationScore(source, fallbackPlayer) {
    return toFiniteNumber(
        source?.overallScore ??
            source?.overallTrustScore ??
            source?.badge?.overallScore ??
            source?.badge?.overallTrustScore ??
            fallbackPlayer?.reputation?.overallScore ??
            fallbackPlayer?.reputation?.overallTrustScore ??
            fallbackPlayer?.reputation,
    );
}

async function fetchJsonIfOk(url, authFetch) {
    try {
        const res = await fetch(url, authFetch);
        return res.ok ? res.json() : null;
    } catch (err) {
        if (authFetch?.signal?.aborted) throw err;
        return null;
    }
}

async function fetchPreviewStats(player, signal) {
    const edgeBasePath = utils.getEdgeBasePath();
    const profileId = player?._id;
    const authFetch = { credentials: "include", signal };
    let steamId = resolvePlayerSteamId(player);

    const profilePayload = profileId
        ? await fetchJsonIfOk(
              `${edgeBasePath}/api/gc/public/profile/${profileId}`,
              authFetch,
          )
        : null;
    const profile = profilePayload?.profile || {};
    steamId = resolvePlayerSteamId(profile) || steamId;

    const reputationPayload = steamId
        ? await fetchJsonIfOk(
              `${edgeBasePath}/api/v1/reputation/${encodeURIComponent(steamId)}`,
              authFetch,
          )
        : null;
    const badge =
        reputationPayload?.badge ||
        reputationPayload?.data?.badge ||
        reputationPayload?.profile ||
        null;
    const stats = profile.playerGeneralStats || {};

    return {
        targetSteamId: steamId,
        kdRatio: toFiniteNumber(stats.kdRatio),
        globalRank: toFiniteNumber(stats.globalRank),
        elo: toFiniteNumber(stats.elo),
        reputation: resolveReputationScore(badge || reputationPayload, player),
        trustTier: badge?.trustTier || null,
        tierColor: badge?.tierColor || null,
        flagImageUrl: profile.flagImageUrl || null,
        membership: profile.membership || null,
        membershipTier: profile.membership?.tier || profile.membershipTier || null,
        accountStatus:
            String(profile.accountStatus || player?.accountStatus || "")
                .trim()
                .toLowerCase() === "verified"
                ? "verified"
                : null,
    };
}

export default function useLobbyPlayerPreview(player, enabled) {
    const key = cacheKey(player);
    const rating3 = player?.rating3?.current ?? player?.rating3 ?? null;
    const playerElo = toFiniteNumber(player?.elo);
    const [preview, setPreview] = useState(() => {
        const cached = previewCache.get(key);
        return {
            loading: !cached,
            targetSteamId:
                cached?.targetSteamId ?? resolvePlayerSteamId(player),
            kdRatio: cached?.kdRatio ?? null,
            globalRank: cached?.globalRank ?? null,
            elo: cached?.elo ?? playerElo,
            reputation:
                cached?.reputation ??
                resolveReputationScore(null, player),
            trustTier: cached?.trustTier ?? null,
            tierColor: cached?.tierColor ?? null,
            rating3,
            flagImageUrl: cached?.flagImageUrl ?? player?.flagImageUrl ?? null,
            membership: cached?.membership ?? player?.membership ?? null,
            membershipTier:
                cached?.membershipTier ??
                player?.membershipTier ??
                player?.membership?.tier ??
                null,
            accountStatus:
                cached?.accountStatus ??
                (String(player?.accountStatus || "").trim().toLowerCase() ===
                "verified"
                    ? "verified"
                    : null),
        };
    });

    useEffect(() => {
        if (!enabled || !key) return undefined;

        const cached = previewCache.get(key);
        if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
            setPreview({
                loading: false,
                targetSteamId:
                    cached.targetSteamId ?? resolvePlayerSteamId(player),
                kdRatio: cached.kdRatio,
                globalRank: cached.globalRank,
                elo: cached.elo ?? playerElo,
                reputation: cached.reputation,
                trustTier: cached.trustTier,
                tierColor: cached.tierColor,
                rating3,
                flagImageUrl: cached.flagImageUrl ?? player?.flagImageUrl ?? null,
                membership: cached.membership ?? player?.membership ?? null,
                membershipTier:
                    cached.membershipTier ??
                    player?.membershipTier ??
                    player?.membership?.tier ??
                    null,
                accountStatus:
                    cached.accountStatus ??
                    (String(player?.accountStatus || "").trim().toLowerCase() ===
                    "verified"
                        ? "verified"
                        : null),
            });
            return undefined;
        }

        const controller = new AbortController();
        setPreview((prev) => ({ ...prev, loading: true, rating3, elo: prev.elo ?? playerElo }));

        fetchPreviewStats(player, controller.signal)
            .then((next) => {
                if (controller.signal.aborted) return;
                previewCache.set(key, { ...next, ts: Date.now() });
                setPreview({
                    loading: false,
                    ...next,
                    elo: next.elo ?? playerElo,
                    rating3,
                });
            })
            .catch(() => {
                if (controller.signal.aborted) return;
                setPreview((prev) => ({ ...prev, loading: false, rating3 }));
            });

        return () => controller.abort();
    }, [
        enabled,
        key,
        player?._id,
        player?.steamId,
        player?.steamId64,
        player?.steam64Id,
        rating3,
        playerElo,
    ]);

    return preview;
}
